import { useEffect, useState } from 'react';
import { registro } from '../../api/registro';
import type { VoceRegistroLetta } from '../../api/registro';
import { formattaDataOra } from '../../lib/formato';

interface Props {
  /** Nome della tabella nel database: è la chiave con cui il registro è indicizzato. */
  tabella: string;
  chiave: string;
}

/*  Lo storico di una riga, mostrato dove serve.

    Un componente e non una schermata, perché la domanda «chi ha cambiato questo» nasce
    davanti al documento e non in un archivio: si apre la scheda dell'ordine e si guarda
    lì. La stessa cosa serve poi nella schermata del registro, dove si arriva partendo da
    una persona o da un giorno invece che da un documento — e allora è lo stesso
    componente con altri argomenti.

    Il valore assente si scrive «vuoto» e non si lascia in bianco: una cella vuota accanto
    a una freccia sembra un difetto di caricamento, mentre «vuoto → BI» dice che quel
    campo prima non era compilato.
*/
export function StoricoModifiche({ tabella, chiave }: Props) {
  const [voci, setVoci] = useState<VoceRegistroLetta[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;
    setVoci(null);
    setErrore(null);

    registro
      .perRiga(tabella, chiave)
      .then((v) => !annullato && setVoci(v))
      .catch((e: Error) => !annullato && setErrore(e.message));

    return () => {
      annullato = true;
    };
  }, [tabella, chiave]);

  if (errore !== null) return <p className="errore">{errore}</p>;
  if (voci === null) return <p className="attesa">Leggo il registro…</p>;

  if (voci.length === 0) {
    /*  Distinguere «non è mai stato modificato» da «il registro non c'era ancora» conta:
        le righe scritte prima che il registro esistesse non hanno storia, e senza questa
        frase sembrerebbero non essere mai state toccate.  */
    return (
      <p className="nota">
        Nessuna modifica registrata. Le righe create prima dell'attivazione del registro
        non hanno storia.
      </p>
    );
  }

  return (
    <ol className="storico">
      {voci.map((voce) => (
        <li key={voce.id}>
          <div className="quando">
            <strong>{voce.operazione}</strong>
            <span>{formattaDataOra(voce.avvenuta)}</span>
            <em>{voce.nomeAutore ?? voce.autore}</em>
          </div>

          {voce.campi.length > 0 && (
            <table className="campi">
              <tbody>
                {voce.campi.map((campo) => (
                  <tr key={campo.campo}>
                    <th scope="row">{campo.campo}</th>
                    <td className={campo.prima === null ? 'assente' : ''}>
                      {campo.prima ?? 'vuoto'}
                    </td>
                    <td className="freccia" aria-hidden="true">
                      →
                    </td>
                    <td className={campo.dopo === null ? 'assente' : ''}>
                      {campo.dopo ?? 'vuoto'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </li>
      ))}
    </ol>
  );
}
